// ─── MSAs & Counties Manager ──────────────────────────────────────────────
// Settings sub-panel that lets acquisitions edit the msa_definitions table
// live. Each row is one MSA, with a chip editor for its counties. Add /
// rename / delete MSAs; add / remove counties per MSA.
//
// After any edit, downstream rows (deals / properties / comps) don't
// automatically retag — clicking "Re-derive all rows" reads every row,
// looks up the current county → MSA mapping, and rewrites msas[] + market.

import { useEffect, useState } from "react";
import { supabase } from "../../supabase";

const COUNTY_INPUT_HELPTEXT = "Format: 'County, ST' — e.g. 'Palm Beach, FL'. Enter or comma splits.";

export default function MsaManagerPanel() {
  const [msas, setMsas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [newMsaName, setNewMsaName] = useState("");
  const [rederiving, setRederiving] = useState(false);

  const flash = (msg, kind = "success") => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 2500);
  };

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from("msa_definitions")
      .select("*")
      .order("display_order")
      .order("name");
    setMsas(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function updateRow(id, patch) {
    const { error } = await supabase.from("msa_definitions").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) { flash(`Save failed: ${error.message}`, "error"); return false; }
    return true;
  }

  async function handleRename(id, newName) {
    const clean = (newName || "").trim();
    if (!clean) return;
    const ok = await updateRow(id, { name: clean });
    if (ok) { flash(`Renamed → "${clean}"`); load(); }
  }

  async function handleToggleActive(row) {
    const ok = await updateRow(row.id, { active: !row.active });
    if (ok) { flash(row.active ? "Deactivated" : "Activated"); load(); }
  }

  async function handleDelete(row) {
    if (!window.confirm(`Delete MSA "${row.name}"?\n\nDeals / properties / comps currently tagged with this MSA will lose the tag until you run "Re-derive all rows".`)) return;
    const { error } = await supabase.from("msa_definitions").delete().eq("id", row.id);
    if (error) { flash(`Delete failed: ${error.message}`, "error"); return; }
    flash(`Deleted "${row.name}"`);
    load();
  }

  async function handleCreate() {
    const clean = (newMsaName || "").trim();
    if (!clean) return;
    const { error } = await supabase.from("msa_definitions").insert({ name: clean, counties: [], display_order: 500 });
    if (error) { flash(`Create failed: ${error.message}`, "error"); return; }
    setNewMsaName("");
    flash(`Created "${clean}"`);
    load();
  }

  async function handleAddCounties(row, rawInput) {
    // Split by comma OR newline; each token should look like "County, ST"
    const tokens = String(rawInput || "")
      .split(/[\n]+|(?<=[A-Z]{2})\s*,\s*(?=[A-Z][a-z])/g)   // split on newline OR ", <Capitalized>" (between entries)
      .map(t => t.trim()).filter(Boolean);
    // Fallback simpler split if the regex missed
    const entries = tokens.length > 0 ? tokens : String(rawInput || "").split(",").map(t => t.trim()).filter(Boolean);
    const existing = new Set(row.counties || []);
    const additions = [];
    for (const e of entries) {
      const normalized = normalizeCountyEntry(e);
      if (!normalized) continue;
      if (!existing.has(normalized)) { existing.add(normalized); additions.push(normalized); }
    }
    if (additions.length === 0) return;
    const ok = await updateRow(row.id, { counties: [...existing] });
    if (ok) { flash(`Added ${additions.length} count${additions.length === 1 ? "y" : "ies"} → ${row.name}`); load(); }
  }

  async function handleRemoveCounty(row, county) {
    const next = (row.counties || []).filter(c => c !== county);
    const ok = await updateRow(row.id, { counties: next });
    if (ok) { flash(`Removed ${county}`); load(); }
  }

  // Walk deals, properties, comps — for each, rebuild msas[] from the
  // current county→MSA index and overwrite market. Updates only rows
  // whose msas[] actually change.
  async function handleRederiveAll() {
    setRederiving(true);
    try {
      // Load fresh index
      const { data: msaRows } = await supabase.from("msa_definitions").select("name, counties").eq("active", true);
      const idx = new Map();
      for (const row of msaRows || []) for (const c of (row.counties || [])) {
        if (!idx.has(c)) idx.set(c, []); idx.get(c).push(row.name);
      }

      const rederive = async (table) => {
        let updated = 0, from = 0;
        while (true) {
          const { data } = await supabase.from(table).select("id, counties, msas, market").range(from, from + 499).order("id");
          if (!data || data.length === 0) break;
          for (const r of data) {
            const seen = new Set(), out = [];
            for (const c of (r.counties || [])) for (const n of (idx.get(c) || [])) {
              if (!seen.has(n)) { seen.add(n); out.push(n); }
            }
            const marketStr = out.join(", ");
            const same =
              (r.msas || []).length === out.length &&
              (r.msas || []).every((v, i) => v === out[i]) &&
              (r.market || "") === marketStr;
            if (!same) {
              await supabase.from(table).update({ msas: out, market: marketStr }).eq("id", r.id);
              updated++;
            }
          }
          if (data.length < 500) break;
          from += 500;
        }
        return updated;
      };
      const d = await rederive("deals");
      const p = await rederive("properties");
      const c = await rederive("comps");
      flash(`Re-derived: ${d} deals · ${p} properties · ${c} comps`);
    } catch (e) {
      flash(`Re-derive failed: ${e.message}`, "error");
    } finally {
      setRederiving(false);
    }
  }

  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: "#ffffff", border: "1px solid #d8d8d8" }}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-bold" style={{ color: "#333" }}>MSAs & Counties</h3>
          <p className="text-xs mt-0.5" style={{ color: "#888" }}>
            Every deal / property / comp is tagged with the MSA(s) its counties belong to. Edit the mappings here, then click <strong>Re-derive all rows</strong> to push the changes across the database.
          </p>
        </div>
        <button
          onClick={handleRederiveAll}
          disabled={rederiving}
          className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40"
          style={{ color: "#ffffff", backgroundColor: "#e93529" }}
          title="Walk every deal, property, and comp — recompute msas[] and market from the current MSA rules"
        >
          {rederiving ? "Re-deriving…" : "🔄 Re-derive all rows"}
        </button>
      </div>

      {toast && (
        <div className="mb-3 px-3 py-2 rounded-lg text-xs" style={{ color: toast.kind === "error" ? "#e93529" : "#2f6b4d", backgroundColor: toast.kind === "error" ? "rgba(233,53,41,0.08)" : "rgba(68,144,106,0.08)", border: `1px solid ${toast.kind === "error" ? "rgba(233,53,41,0.25)" : "rgba(68,144,106,0.25)"}` }}>
          {toast.msg}
        </div>
      )}

      {/* Create-new-MSA row */}
      <div className="flex items-center gap-2 mb-4 pb-3" style={{ borderBottom: "1px solid #f4f4f4" }}>
        <input
          value={newMsaName}
          onChange={(e) => setNewMsaName(e.target.value)}
          placeholder="+ Add MSA (e.g. Raleigh-Durham)"
          className="flex-1 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pp-red/40"
          style={{ border: "1px solid #d8d8d8", color: "#333" }}
          onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
        />
        <button
          onClick={handleCreate}
          disabled={!newMsaName.trim()}
          className="text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-40"
          style={{ color: "#ffffff", backgroundColor: "#e93529" }}
        >
          Add MSA
        </button>
      </div>

      {loading ? (
        <p className="text-xs" style={{ color: "#888" }}>Loading…</p>
      ) : (
        <div className="space-y-2">
          {msas.map(row => <MsaRow key={row.id} row={row} onRename={handleRename} onToggleActive={handleToggleActive} onDelete={handleDelete} onAddCounties={handleAddCounties} onRemoveCounty={handleRemoveCounty} />)}
        </div>
      )}
      <p className="text-[10px] mt-3" style={{ color: "#aaa" }}>{COUNTY_INPUT_HELPTEXT}</p>
    </div>
  );
}

function MsaRow({ row, onRename, onToggleActive, onDelete, onAddCounties, onRemoveCounty }) {
  const [expanded, setExpanded] = useState(false);
  const [nameDraft, setNameDraft] = useState(row.name);
  const [addDraft, setAddDraft] = useState("");
  useEffect(() => { setNameDraft(row.name); }, [row.name]);
  const isActive = row.active !== false;

  return (
    <div className="rounded-lg" style={{ background: isActive ? "#fafafa" : "#f4f4f4", border: "1px solid #e4e4e4", opacity: isActive ? 1 : 0.55 }}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          onClick={() => setExpanded(v => !v)}
          className="text-xs flex-shrink-0"
          style={{ color: "#888", transform: expanded ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}
        >▶</button>
        <input
          value={nameDraft}
          onChange={e => setNameDraft(e.target.value)}
          onBlur={() => { if (nameDraft.trim() && nameDraft !== row.name) onRename(row.id, nameDraft); }}
          onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
          className="text-sm font-semibold bg-transparent focus:outline-none focus:border-b focus:border-pp-red flex-1"
          style={{ color: "#333" }}
        />
        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: "#f4f4f4", color: "#666" }}>
          {(row.counties || []).length} count{(row.counties || []).length === 1 ? "y" : "ies"}
        </span>
        {row.region && <span className="text-[10px]" style={{ color: "#888" }}>[{row.region}]</span>}
        <button
          onClick={() => onToggleActive(row)}
          className="text-[10px] px-2 py-0.5 rounded"
          style={{ color: isActive ? "#2f6b4d" : "#888", background: isActive ? "rgba(68,144,106,0.08)" : "#eee" }}
        >
          {isActive ? "Active" : "Inactive"}
        </button>
        <button
          onClick={() => onDelete(row)}
          className="text-[10px] px-2 py-0.5 rounded hover:bg-[rgba(233,53,41,0.14)] transition-colors"
          style={{ color: "#e93529" }}
        >
          Delete
        </button>
      </div>

      {expanded && (
        <div className="px-3 pb-3">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {(row.counties || []).length === 0 ? (
              <span className="text-[10px]" style={{ color: "#aaa" }}>No counties yet — add below.</span>
            ) : (row.counties || []).map(c => (
              <span key={c} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full" style={{ background: "#ffffff", border: "1px solid #d8d8d8", color: "#333" }}>
                {c}
                <button
                  onClick={() => onRemoveCounty(row, c)}
                  className="text-[#8a8a8a] hover:text-[#e93529] transition-colors"
                  title="Remove"
                >✕</button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input
              value={addDraft}
              onChange={e => setAddDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && addDraft.trim()) { onAddCounties(row, addDraft); setAddDraft(""); } }}
              placeholder="Add county — 'Palm Beach, FL' (Enter or paste multiple)"
              className="flex-1 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-pp-red/30"
              style={{ border: "1px solid #d8d8d8", color: "#333" }}
            />
            <button
              onClick={() => { if (addDraft.trim()) { onAddCounties(row, addDraft); setAddDraft(""); } }}
              disabled={!addDraft.trim()}
              className="text-xs font-semibold px-3 py-1 rounded disabled:opacity-40"
              style={{ color: "#333", background: "#f4f4f4", border: "1px solid #d8d8d8" }}
            >
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Normalize a raw county input to "<County>, <ST>" — strips trailing " County",
// upper-cases the state. Returns null if we can't parse.
function normalizeCountyEntry(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const m = s.match(/^\s*(.+?)\s*,\s*([A-Za-z]{2})\s*$/);
  if (!m) return null;
  let county = m[1].replace(/\s+County$/i, "").trim();
  const st = m[2].toUpperCase();
  return county ? `${county}, ${st}` : null;
}
